// auth.js
// Copy right - © 2024 tecera gmbh

import { writable, get } from 'svelte/store';
import { BASE_API_URL } from '../config.js';
import { navigate } from "svelte-routing";


export const is_authenticated = writable(false);
export const user_role = writable(null);
export const available_roles = writable([]);
export const user_permissions = writable(null);
export const login_info_message = writable(null);
export const login_error_message = writable(null);

function handle_bad_response(response) {
    if (response.status === 401) {
        let message = "Ihre Session ist abgelaufen. Bitte loggen sie sich erneut ein.";
        logout({ error_message: message });

    }
    throw new Error(`HTTP error! status: ${response.status}`);
}


export async function authenticate() {
    const token = sessionStorage.getItem('2kai_access_token');
    if (!token) {
        let message = "Ungültiges Token"
        logout({ error_message: message });
    }

    let post_data = { "token": token };

    try {
        let url_extension = `/validate_token`;
        let request_url = BASE_API_URL + url_extension;
        const response = await fetch(request_url, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(post_data),
            credentials: "include",
        });
        const data = await response.json(); // Convert response to JSON

        if (response.status === 200) {
            is_authenticated.set(true);
            user_role.set(data.mapview_role);
            available_roles.set(data.available_roles);
            user_permissions.set(data.mapview_permissions);
            return true;
        } else {
            handle_bad_response(response);
            return false;
        }
    } catch (error) {
        let message = "Ein Fehler ist aufgetreten."
        logout({ error_message: message });
    }
}

/**
 * 
 * @param {String} permission_name - The name of the permission to check.
 * @returns Wether the user has the permission or not.
 */
export function check_permission(permission_name) {
    try {
        let permissions = get(user_permissions);
        let needed_permission = permissions?.[permission_name];
        return needed_permission === 1;

    } catch (error) {
        let message = "Ein Fehler ist aufgetreten."
        logout({ error_message: message });
    }
}

/**
 * Reroute a user trying to access a forbidden page. Try different available pages before logging him out.
 */
export function rerout_user() {
    let permissions = get(user_permissions);
    let role = get(user_role);
    if (permissions["access_hymo"] && permissions["access_hymo"] === 1) {
        navigate("/pegel", { replace: true });
    } else if (permissions["access_water_meter"] && permissions["access_water_meter"] === 1) {
        navigate("/wasserzaehler", { replace: true });
    } else if (role["view_assembly"] && role["view_assembly"] === 1) {
        navigate("/montage", { replace: true });
    } else {
        let message = "Fehlende Berechtigungen. Bitte wenden sie sich an einen Administrator.";
        logout({ error_message: message })
    }
}

export async function check_if_authenticated() {
    if (!get(is_authenticated)) {
        await authenticate();
    }
}

export function logout({ error_message = null, warningMessage = null, info_message = null } = {}) { //TODO: REMOVE EVERYTHING (DATA) FROM STORES
    navigate("/", { replace: true });
    sessionStorage.removeItem("2kai_access_token");
    is_authenticated.set(false);
    if (error_message) {
        login_error_message.set(error_message);
    } else if (info_message) {
        login_info_message.set(info_message);
    }
}

/**
 * Login API call.  
 *
 * @param {string} username - The username of the user.
 * @param {string} password - The password of the user.
 * @returns {Promise} A promise that resolves to an object with the status of the login attempt, and an error message in case of failure.
 */
export async function login(username, password) {
    let url_extension = "/login";
    let request_url = BASE_API_URL + url_extension;
    let post_data = { username, password };

    try {
        const response = await fetch(request_url, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(post_data),
            credentials: "include",
        });

        if (response.status === 200) {
            const data = await response.json();
            sessionStorage.setItem("2kai_access_token", data.token);
            is_authenticated.set(true);  // authenticate aswell to not make another API call
            // setting of user related store variables, only done on login atm
            user_role.set(data.mapview_role);
            available_roles.set(data.available_roles);
            user_permissions.set(data.mapview_permissions);

            return { "status": "success" };
        } else {
            const data = await response.json();
            return { "status": "error", "message": data.error };
        }
    } catch (error) {
        return { "status": "error", "message": "Server Fehler" };
    }
}

// register -> aktivierungsmail
export async function register(username, password, password_confirm) {
    let url_extension = "/register";
    let request_url = BASE_API_URL + url_extension;
    let post_data = {
        username: username,
        password: password,
        password_confirm: password_confirm,
    };

    try {
        const response = await fetch(request_url, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(post_data),
            credentials: "include",
        });

        if (response.status === 201) {
            const data = await response.json();
            return { "status": "success" };
        } else {
            const data = await response.json();
            return { "status": "error", "message": data.error };
        }
    } catch (error) {
        return { "status": "error", "message": "Server Fehler" };
    }
}

// check if user is registered
export async function check_if_email_is_registered(username) {
    let url_extension = "/check_email";
    let request_url = BASE_API_URL + url_extension;
    let post_data = {
        username: username,
    };

    try {
        const response = await fetch(request_url, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(post_data),
            credentials: "include",
        });

        if (response.status === 200) {
            const data = await response.json();
            if (data.success == true) {
                return { "status": "success", "message": data.message };
            }
            return { "status": "error", "message": data.error };
        }
    } catch (error) {
        return { "status": "error", "message": "Server Fehler" };
    }
}

// check if user is registered
export async function reset_password(username,password,code) {
    let url_extension = "/reset_password";
    let request_url = BASE_API_URL + url_extension;
    let post_data = {username,password,code};

    try {
        const response = await fetch(request_url, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(post_data),
            credentials: "include",
        });

        if (response.status === 200) {
            const data = await response.json();
            if (data.success == true) {
                return { "status": "success", "message": data.message };
            }
            return { "status": "error", "message": data.error };
        }
    } catch (error) {
        return { "status": "error", "message": "Server Fehler" };
    }
}